import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { BadgeColor } from './types/badgeTypes';
import { Tooltip } from '@mui/material';
import { PiCheckLight } from 'react-icons/pi';

interface BadgeProps {
  color?: BadgeColor;
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
  tooltip?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  selected?: boolean;
}

const colorStyles: Record<
  NonNullable<BadgeProps['color']>,
  {
    light: {
      bg: string;
      text: string;
      ring: string;
    };
    dark: {
      bg: string;
      text: string;
      ring: string;
    };
  }
> = {
  gray: {
    light: { bg: 'bg-surface-sunken', text: 'text-fg-body', ring: 'ring-hairline' },
    dark: { bg: '', text: '', ring: '' },
  },
  red: {
    light: { bg: 'bg-danger-100', text: 'text-danger-600', ring: 'ring-danger-300/50' },
    dark: { bg: 'dark:bg-danger-600/20', text: 'dark:text-danger-300', ring: 'dark:ring-danger-500/50' },
  },
  yellow: {
    light: { bg: 'bg-warning-100', text: 'text-warning-700', ring: 'ring-warning-300/50' },
    dark: { bg: 'dark:bg-warning-600/20', text: 'dark:text-warning-300', ring: 'dark:ring-warning-500/50' },
  },
  green: {
    light: { bg: 'bg-success-100', text: 'text-success-600', ring: 'ring-success-300/50' },
    dark: { bg: 'dark:bg-success-600/20', text: 'dark:text-success-300', ring: 'dark:ring-success-500/50' },
  },
  blue: {
    light: { bg: 'bg-info-100', text: 'text-info-600', ring: 'ring-info-300/50' },
    dark: { bg: 'dark:bg-info-600/20', text: 'dark:text-info-300', ring: 'dark:ring-info-500/50' },
  },
  indigo: {
    light: { bg: 'bg-indigo-100', text: 'text-indigo-600', ring: 'ring-indigo-300/50' },
    dark: { bg: 'dark:bg-indigo-600/20', text: 'dark:text-indigo-300', ring: 'dark:ring-indigo-500/50' },
  },
  purple: {
    light: { bg: 'bg-purple-100', text: 'text-purple-600', ring: 'ring-purple-300/50' },
    dark: { bg: 'dark:bg-purple-600/20', text: 'dark:text-purple-300', ring: 'dark:ring-purple-500/50' },
  },
  pink: {
    light: { bg: 'bg-pink-100', text: 'text-pink-600', ring: 'ring-pink-300/50' },
    dark: { bg: 'dark:bg-pink-600/20', text: 'dark:text-pink-300', ring: 'dark:ring-pink-500/50' },
  },
  orange: {
    light: { bg: 'bg-warning-100', text: 'text-warning-600', ring: 'ring-warning-300/50' },
    dark: { bg: 'dark:bg-warning-600/20', text: 'dark:text-warning-300', ring: 'dark:ring-warning-500/50' },
  },
  cyan: {
    light: { bg: 'bg-cyan-100', text: 'text-cyan-600', ring: 'ring-cyan-300/50' },
    dark: { bg: 'dark:bg-cyan-600/20', text: 'dark:text-cyan-300', ring: 'dark:ring-cyan-500/50' },
  },
  black: {
    light: { bg: 'bg-gray-900', text: 'text-white', ring: 'ring-gray-700/50' },
    dark: { bg: 'dark:bg-gray-900', text: 'dark:text-white', ring: 'dark:ring-gray-700/50' },
  },
  slate: {
    light: { bg: "bg-slate-100", text: "text-slate-700", ring: "ring-slate-200/60" },
    dark: { bg: "dark:bg-slate-800/60", text: "dark:text-slate-200", ring: "dark:ring-slate-200/60" },
  },
  amber: {
    light: { bg: "bg-warning-50", text: "text-warning-700", ring: "ring-warning-200/60" },
    dark: { bg: "dark:bg-warning-900/30", text: "dark:text-warning-200", ring: "dark:ring-warning-200/60" },
  },
  emerald: {
    light: { bg: "bg-success-50", text: "text-success-700", ring: "ring-success-200/60" },
    dark: { bg: "dark:bg-success-900/30", text: "dark:text-success-200", ring: "dark:ring-success-200/60" },
  },
  violet: {
    light: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200/60" },
    dark: { bg: "dark:bg-violet-900/30", text: "dark:text-violet-200", ring: "dark:ring-violet-200/60" },
  },
  teal: {
    light: { bg: "bg-teal-50", text: "text-teal-700", ring: "ring-teal-200/60" },
    dark: { bg: "dark:bg-teal-900/30", text: "dark:text-teal-200", ring: "dark:ring-teal-200/60" },
  },
  sky: {
    light: { bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-200/60" },
    dark: { bg: "dark:bg-sky-900/30", text: "dark:text-sky-200", ring: "dark:ring-sky-200/60" },
  },
};

const selectedRingMap: Record<NonNullable<BadgeProps['color']>, string> = {
  gray: 'ring-champagne',
  red: 'ring-danger-400 dark:ring-danger-400',
  yellow: 'ring-warning-500 dark:ring-warning-400',
  green: 'ring-success-500 dark:ring-success-400',
  blue: 'ring-info-500 dark:ring-info-400',
  indigo: 'ring-indigo-500 dark:ring-indigo-400',
  purple: 'ring-purple-500 dark:ring-purple-400',
  pink: 'ring-pink-500 dark:ring-pink-400',
  orange: 'ring-warning-500 dark:ring-warning-400',
  cyan: 'ring-cyan-500 dark:ring-cyan-400',
  black: 'ring-gray-600 dark:ring-gray-400',
  slate: 'ring-slate-400 dark:ring-slate-400',
  amber: 'ring-warning-500 dark:ring-warning-400',
  emerald: 'ring-success-500 dark:ring-success-400',
  violet: 'ring-violet-500 dark:ring-violet-400',
  teal: 'ring-teal-500 dark:ring-teal-400',
  sky: 'ring-sky-500 dark:ring-sky-400',
};

const Badge: React.FC<BadgeProps> = ({ color = 'gray', children, onClose, className, tooltip, disabled = false, icon, selected = false }) => {
  const { t } = useTranslation();
  const styles = colorStyles[color];

  const classes = clsx(
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium gap-1.5 shadow-sm',
    selected ? 'ring-2 shadow-md' : 'ring-1 ring-inset',
    selected ? selectedRingMap[color] : [styles.light.ring, styles.dark.ring],
    styles.light.bg,
    styles.light.text,
    styles.dark.bg,
    styles.dark.text,
    'w-fit',
    disabled ? 'opacity-50 cursor-not-allowed' : '',
    className
  );

  return (
    <Tooltip title={tooltip || ''}>
      <span className={classes}>
        {selected && <PiCheckLight className="w-3 h-3 shrink-0" />}
        {icon && !selected && <span className="text-sm">{icon}</span>}
        {children}
        {onClose && !disabled && (
          <button
            type="button"
            className="ms-1 text-xs text-fg-muted hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60 rounded"
            onClick={onClose}
            aria-label={t('Remove badge')}
          >
            ✕
          </button>
        )}
      </span>
    </Tooltip>
  );
};

export default Badge;